// @owners { team: patients-team }
import { BORDERS, COLORS, SHADOWS, SIZES, SPACING } from '@alto/design-library-tokens';
import { Column, MdSpacing, Pressable, Row, SmSpacing, XxlSpacing } from '@alto/design-system';
import { AddOnsContext, useQueryEssentials } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useEssentialsAddress } from '~shared/features/essentials';
import { type OriginValues } from '~shared/features/essentials/add_ons_consts';
import { getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { getURLParams } from '~shared/helpers/helper';
import { usePrevious } from '~shared/hooks';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Address, type Shipment } from '~shared/types';
import { ProductTitle } from './ProductTitle';
import { Carousel, ErrorPage, FullPage, LoadingPage, ProductFooter } from '~web/features/essentials/components';
import { useTrackViewedEvent } from '~web/features/essentials/hooks';
import { MissingAddress } from '~web/features/essentials/pages/StoreProducts/MissingAddress';
import { Icon, Image, Text } from '~web/features/essentials/primitives';
import { useDeprecatedMediaQuery, useDeprecatedWindowResize } from '~web/hooks';

const StyledImagePreview = styled.div<{ active: boolean }>`
  padding: ${SPACING.STATIC.XS.px};
  border-radius: ${BORDERS.RADIUS.LG.px};
  background-color: ${COLORS.BACKGROUND_COLORS.WHITE};
  opacity: ${({ active }) => (active ? 1 : 0.5)};
  margin: ${SPACING.STATIC.XXS.px};
  &:hover {
    cursor: pointer;
    background-color: ${({ active }) =>
      active ? COLORS.BACKGROUND_COLORS.WHITE : COLORS.BACKGROUND_COLORS.PRIMARY_LIGHT};
  }
  ${({ active }) => active && SHADOWS.BOTTOM_LIGHT}
`;

const StyledMainImage = styled.img`
  max-width: 100%;
  max-height: 500px;
  object-fit: contain;
`;

type Props = {
  readonly id: number;
  readonly address: Address;
  readonly shipment: Shipment | undefined;
  readonly origin: OriginValues | undefined;
};
const Content = ({ id, address, shipment, origin }: Props) => {
  const location = useLocation();
  const analyticsProps = location.state.analyticsProps;
  const { goBack } = useNavigation<'RouteEssentialsDetails'>();
  const { isLoading, error, essentialsHash } = useQueryEssentials({
    addressID: address.id,
    date: shipment?.scheduled_date,
  });
  const product = essentialsHash[id];
  useTrackViewedEvent(EVENTS.ESSENTIALS__PRODUCT_DETAILS_VIEWED, shipment?.id, origin, analyticsProps);
  const [imageIndex, setImageIndex] = useState(0);
  const prevImageIndex = usePrevious(imageIndex);
  const dispatch = useDispatchShared();
  const isMediumScreen = useDeprecatedMediaQuery('MD');
  const isMobile = !useDeprecatedMediaQuery('SM');
  const isLargeScreen = useDeprecatedMediaQuery('LG');
  const windowSize = useDeprecatedWindowResize();
  const defaultViewSidePadding = isMobile ? SPACING.STATIC.LG.px : SPACING.STATIC.XXL.px;
  const sidePadding = isLargeScreen
    ? `${Math.max((windowSize.width - parseFloat(SIZES.PAGE.WEB_NAVBAR_WIDTH.LG)) / 2, parseFloat(defaultViewSidePadding))}px`
    : defaultViewSidePadding;

  const addOnsContext = useMemo(
    () =>
      ({
        shipmentID: shipment?.id,
        origin,
      }) as const,
    [shipment?.id, origin],
  );

  const handleImagePress = (index: number) => () => {
    setImageIndex(index);
  };

  const recordImageViewedEvent = useCallback(
    (index: number, prevIndex?: number) => {
      dispatch(
        sendAnalyticsEvent(
          createEvent(EVENTS.ESSENTIALS__OTC_DETAILS_CAROUSEL_IMAGE_VIEWED, {
            ...analyticsProps,
            img_number: index,
            referral_img_number: prevIndex,
          }),
        ),
      );
    },
    [dispatch, analyticsProps],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    recordImageViewedEvent(imageIndex, prevImageIndex);
  }, [imageIndex, prevImageIndex, recordImageViewedEvent]);

  if (isLoading) return <LoadingPage />;
  if (error || !product)
    return (
      <ErrorPage
        title="Unable to load Essential"
        description={`${error}`}
      />
    );
  return (
    <FullPage>
      <AddOnsContext.Provider value={addOnsContext}>
        <Column
          flexBasis="50%"
          backgroundColor={COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST}
          style={{
            paddingTop: parseFloat(SPACING.STATIC.LG.px),
            paddingRight: parseFloat(SPACING.STATIC.LG.px),
            paddingBottom: parseFloat(SPACING.STATIC.LG.px),
            paddingLeft: parseFloat(SPACING.STATIC.LG.px),
          }}
        >
          <Pressable onPress={goBack}>
            <Row centerVertically>
              <Icon
                name="chevronleft"
                size="SM"
                interactive
                label="back icon"
              />
              <SmSpacing />
              <Text
                title="Back"
                variant="body-semibold"
                color="PRIMARY"
              />
            </Row>
          </Pressable>
          {isMobile ? (
            <Carousel.Container
              numOfItems={product.image_urls.length}
              numOfColumns={1}
              iconSize="LG"
              onChange={recordImageViewedEvent}
              showDots
            >
              {product.image_urls.map((image) => (
                <Carousel.Item
                  key={image}
                  numOfColumns={1}
                >
                  <Row
                    center
                    wrap
                  >
                    <Image
                      src={image}
                      alt={product.name}
                      width="100%"
                    />
                  </Row>
                </Carousel.Item>
              ))}
            </Carousel.Container>
          ) : (
            <Column
              center
              flexGrow={1}
            >
              <StyledMainImage
                src={product.image_urls[imageIndex]}
                alt={product.name}
              />
              <Row
                center
                wrap
              >
                {product.image_urls.map((image, index) => (
                  <StyledImagePreview
                    key={`${image} preview`}
                    onClick={handleImagePress(index)}
                    active={index === imageIndex}
                  >
                    <Image
                      src={image}
                      alt={product.name}
                      width={SIZES.ILLUSTRATION.MD.px}
                      height={SIZES.ILLUSTRATION.MD.px}
                    />
                  </StyledImagePreview>
                ))}
              </Row>
            </Column>
          )}
        </Column>
        <Column
          centerVertically
          flexBasis="50%"
          style={{
            paddingTop: parseFloat(defaultViewSidePadding),
            paddingRight: parseFloat(sidePadding),
            paddingBottom: parseFloat(defaultViewSidePadding),
            paddingLeft: parseFloat(defaultViewSidePadding),
          }}
        >
          <ProductTitle
            product={product}
            isMobile={isMobile}
          />
          <MdSpacing />
          <Text
            title={product.quantity_description || ''}
            variant="body-semibold"
          />
          <MdSpacing />
          <Text
            title={product.product_description || ''}
            variant="body"
          />
          <XxlSpacing />
          <Column>
            <Column flexBasis={isMediumScreen ? '360px' : '100%'}>
              <ProductFooter
                product={product}
                analyticsProps={analyticsProps}
                onClose={goBack}
                showPrice
              />
            </Column>
          </Column>
        </Column>
      </AddOnsContext.Provider>
    </FullPage>
  );
};

type StoreProductRouteParams = {
  readonly id: string;
};

export const StoreProduct = () => {
  const { id } = useParams<StoreProductRouteParams>();
  const { order, origin } = getURLParams();
  const shipmentID = Number(order);
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID }));
  const address = useEssentialsAddress({ shipmentID });
  const addressLoading = useSelectorShared((state) => state.addresses.lastRequestSucceededAt === 0);

  if (addressLoading) {
    return (
      <FullPage>
        <LoadingPage />
      </FullPage>
    );
  }

  if (!address) {
    return <MissingAddress shipment={shipment} />;
  }

  return (
    <FullPage>
      <Content
        id={Number(id)}
        address={address}
        shipment={shipment}
        origin={origin}
      />
    </FullPage>
  );
};
